<template>
  <div class="minimalist-booking" :class="{ 'compact-booking-header': currentStep > 1 && !isRescheduling }"
    :style="cssVariables">
    <div class="booking-header">
      <div v-if="(currentStep === 1 && !isRescheduling) && business.avatar" class="business-avatar">
        <img :src="business.avatar" alt="Business avatar" />
      </div>
      <h1>{{ business.name }}</h1>
      <div v-if="(currentStep === 1 && !isRescheduling) && (business.shortDescription || previewMetaItems.length)"
        class="business-meta-line">
        <span v-if="business.shortDescription" class="short-description">{{ business.shortDescription }}</span>
        <span v-if="business.shortDescription && previewMetaItems.length" class="meta-divider">|</span>
        <template v-for="(item, index) in previewMetaItems" :key="index">
          <span v-if="index > 0" class="meta-divider">|</span>
          <a :href="item.href" class="social-media-link" target="_blank" rel="noopener">{{
            item.label }}</a>
        </template>
      </div>
      <p v-if="(currentStep === 1 && !isRescheduling) && business.description" class="business-description">{{
        business.description }}</p>
    </div>

    <!-- Error Messages Display -->
    <BaseMessages :response="apiResponse" v-if="apiResponse" max-width="800px" />
    <BaseMessages :response="validationError" v-if="validationError" max-width="800px" />

    <!-- Step 1 -->
    <div v-if="currentStep === 1 && !isRescheduling" class="step-container">
      <div class="step-header">
        <h2>Pakalpojumi</h2>
        <div v-if="services.length > 0" class="step-counter">
          Solis {{ displayStep }}/{{ displayTotalSteps }}
        </div>
      </div>
      <div class="service-list" v-if="services.length > 0">
        <div v-for="service in services" :key="service.id" class="service-card"
          :class="{ 'selected': selectedService === service }" @click="selectService(service)">
          <div class="service-info">
            <div class="service-header">
              <h3>{{ service.name }}</h3>
              <span v-if="service.price && service.price > 0" class="service-price">{{ formatPrice(service.price)
              }}</span>
            </div>
            <p>{{ service.description }}</p>
            <div class="service-meta" v-if="service.duration || (service.price && service.price > 0)">
              <span v-if="service.duration" class="duration">{{ formatDuration(service.duration) }}</span>
              <span v-if="service.price && service.price > 0" class="mobile-price">{{ formatPrice(service.price)
              }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="no-services">Neviens pakalpojums nav pieejams.</div>

      <div class="step-navigation" v-if="services.length > 0">
        <button class="primary-btn" @click="handleNextStep">
          Turpināt
        </button>
      </div>
    </div>

    <!-- Step 2 -->
    <div v-else-if="currentStep === 2 && !isRescheduling" class="step-container compact-step-container">
      <div class="step-header step-header-w-subtitle">
        <h2>Papildinājumi</h2>
        <div class="step-counter">
          Solis {{ displayStep }}/{{ displayTotalSteps }}
        </div>
      </div>
      <p class="addon-subtitle">Izvēlieties vienu vai vairākus papildinājumus, vai arī turpiniet bez tiem.</p>
      <div class="service-list" v-if="filteredAddons.length > 0">
        <div v-for="addon in filteredAddons" :key="addon.id" class="service-card"
          :class="{ 'selected': selectedAddons.some(a => a.id === addon.id) }" @click="toggleAddon(addon)">
          <div class="service-info">
            <div class="service-header">
              <h3>{{ addon.name }}</h3>
              <span v-if="addon.price && addon.price > 0" class="service-price">{{ formatPrice(addon.price)
              }}</span>
            </div>
            <p>{{ addon.description }}</p>
            <div class="service-meta" v-if="addon.duration || (addon.price && addon.price > 0)">
              <span v-if="addon.duration" class="duration">{{ formatDuration(addon.duration) }}</span>
              <span v-if="addon.price && addon.price > 0" class="mobile-price">{{ formatPrice(addon.price)
              }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="step-navigation">
        <button class="secondary-btn back-button" @click="prevStep">
          Atpakaļ
        </button>
        <button class="primary-btn" @click="handleNextStep">
          {{ filteredAddons.length > 0 ? 'Turpināt' : 'Izlaist' }}
        </button>
      </div>
    </div>

    <!-- Step 3 -->
    <div v-else-if="(currentStep === 3 && !isRescheduling) || (currentStep === 1 && isRescheduling)"
      class="step-container compact-step-container">
      <div class="step-header">
        <h2>Speciālisti</h2>
        <div class="step-counter">
          Solis {{ displayStep }}/{{ displayTotalSteps }}
        </div>
      </div>
      <div class="provider-list">
        <div v-for="provider in filteredProviders" :key="provider.id" class="provider-card"
          :class="{ 'selected': selectedProvider === provider }" @click="selectProvider(provider)">
          <div class="provider-info">
            <h3>{{ provider.name }}</h3>
            <p v-if="provider.role">{{ provider.role }}</p>
          </div>
          <div class="provider-select-indicator"></div>
        </div>
      </div>
      <div class="step-navigation">
        <button v-if="!isRescheduling" class="secondary-btn back-button" @click="prevStep">
          Atpakaļ
        </button>
        <button class="primary-btn" @click="handleNextStep">
          Turpināt
        </button>
      </div>
    </div>

    <!-- Step 4 -->
    <div v-else-if="(currentStep === 4 && !isRescheduling) || (currentStep === 2 && isRescheduling)"
      class="step-container compact-step-container">
      <div class="step-header">
        <h2>Datums un laiks</h2>
        <div class="step-counter">
          Solis {{ displayStep }}/{{ displayTotalSteps }}
        </div>
      </div>

      <!-- Loading state -->
      <div v-if="showLoadingSpinner" class="loading-container">
        <div class="loading-spinner"></div>
        <p>Notiek datu ielāde...</p>
      </div>

      <div v-else class="date-picker">
        <div class="calendar">
          <!-- Simple calendar implementation -->
          <div class="month-navigation">
            <h3>{{ currentMonthName }} {{ currentYear }}</h3>
            <div class="month-navigation-buttons">
              <button @click="prevMonth">
                <PhCaretLeft class="month-button" />
              </button>
              <button @click="nextMonth">
                <PhCaretRight class="month-button" />
              </button>
            </div>
          </div>
          <div class="calendar-grid">
            <div v-for="day in 7" :key="`header-${day}`" class="day-header">
              {{ dayNames[(day - 1) % 7] }}
            </div>
            <div v-for="(date, index) in calendarDays" :key="`day-${index}`" class="calendar-day" :class="{
              'empty': !date,
              'available': date && isDateAvailable(date),
              'selected': isSameDate(date, selectedDate),
              'unavailable': date && !isDateAvailable(date)
            }" @click="date && isDateAvailable(date) ? selectDate(date) : null">
              {{ date ? date.getDate() : '' }}
            </div>
          </div>
        </div>

        <div class="time-slots">
          <h3>Pieejamais laiks</h3>
          <div v-if="!selectedDate" class="placeholder-message">
            Lūdzu izvēlieties datumu
          </div>
          <div v-else-if="loadingTimeSlots" class="loading-container">
            <div class="loading-spinner small"></div>
            <p>Notiek datu ielāde...</p>
          </div>
          <div v-else-if="availableTimeSlots.length === 0" class="no-slots-message">
            Šim datumam nav pieejamu laiku. Lūdzu izvēlēties citu datumu.
          </div>
          <div v-else class="time-slot-grid" :class="{ 'has-seats': hasSeatData }">
            <div v-for="slot in availableTimeSlots" :key="slot.id" class="time-slot"
              :class="{ 'selected': selectedTimeSlot === slot, 'with-seats': slot.seatNumber != null && slot.takenSeats != null }"
              @click="selectTimeSlot(slot)">
              <span class="time-slot-time">{{ formatTime(slot.startTime) }}</span>
              <div v-if="slot.seatNumber != null && slot.takenSeats != null" class="seat-info">
                <div class="seat-progress-container">
                  <div class="seat-progress-bar" :style="{ width: getSeatProgressPercent(slot) + '%' }"></div>
                </div>
                <span class="seat-text">{{ slot.seatNumber - slot.takenSeats }} no {{ slot.seatNumber }} vietām {{
                  (slot.seatNumber - slot.takenSeats) === 1 ? 'brīva' : 'brīvas' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="step-navigation">
        <button v-if="!(isRescheduling && (filteredProviders.length == 0 || filteredProviders.length == 1))"
          class="secondary-btn back-button" @click="prevStep">
          Atpakaļ
        </button>
        <button class="primary-btn" @click="handleNextStep">
          Turpināt
        </button>
      </div>
    </div>

    <!-- Step 5: Review and Add Additional Information (Step 3 when rescheduling) -->
    <div v-else-if="(currentStep === 5 && !isRescheduling) || (currentStep === 3 && isRescheduling)"
      class="step-container compact-step-container">
      <div class="step-header">
        <h2>Apstiprināšana</h2>
        <div class="step-counter">
          Solis {{ displayStep }}/{{ displayTotalSteps }}
        </div>
      </div>
      <div class="booking-summary-margins">
        <BookingSummary :service="selectedService" :selectedAddons="selectedAddons" :provider="selectedProvider"
          :date="selectedDate" :timeSlot="selectedTimeSlot" :show-provider="filteredProviders.length > 1"
          :show-duration="true" :primary-light="business?.colorScheme?.primaryLight" />
      </div>

      <!-- Client info section -->
      <div v-if="!isRescheduling" class="client-info">
        <div class="form-row">
          <div class="form-group">
            <label for="firstName">Vārds</label>
            <input type="text" id="firstName" v-model="clientInfo.firstName" placeholder="Ievadiet savu vārdu"
              class="input-field">
          </div>
          <div class="form-group">
            <label for="lastName">Uzvārds</label>
            <input type="text" id="lastName" v-model="clientInfo.lastName" placeholder="Ievadiet savu uzvārdu"
              class="input-field">
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="email">E-pasts</label>
            <input type="email" id="email" v-model="clientInfo.email" placeholder="Ievadiet savu e-pastu"
              class="input-field">
          </div>
          <div class="form-group">
            <label for="phone">Telefona numurs</label>
            <input type="tel" id="phone" v-model="clientInfo.phone" placeholder="Ievadiet savu telefona numuru"
              class="input-field">
          </div>
        </div>
        <label class="checkbox-label">
          <input type="checkbox" v-model="saveToLocalStorage" />
          <span class="checkbox-text">Saglabāt manus datus šajā ierīcē</span>
        </label>
      </div>

      <div class="step-navigation">
        <button class="secondary-btn back-button" @click="prevStep">
          Atpakaļ
        </button>
        <button class="primary-btn" @click="isRescheduling ? confirmReschedule() : confirmBooking()">
          Saglabāt
        </button>
      </div>
    </div>

    <!-- Step 6: Confirmation (Step 4 when rescheduling) -->
    <div v-else-if="(currentStep === 6 && !isRescheduling) || (currentStep === 4 && isRescheduling)"
      class="step-container compact-step-container">
      <div class="confirmation-header">
        <h2>{{ isRescheduling ? 'Pieraksts pārplānots' : 'Pieraksts izveidots' }}</h2>
        <div class="checkmark-circle">
          <PhCheck />
        </div>
      </div>
      <p class="confirmation-subtitle">Pēc brīža jūs saņemsiet arī e-pastu ar pieraksta detaļām.</p>

      <div class="confirmation-details">
        <BookingSummary :service="selectedService" :selectedAddons="selectedAddons" :provider="selectedProvider"
          :date="selectedDate" :timeSlot="selectedTimeSlot" :client-info="clientInfo"
          :show-provider="filteredProviders.length > 1" :show-client-info="!isRescheduling"
          :primary-light="business?.colorScheme?.primaryLight" />
      </div>
    </div>
  </div>
</template>

<script>
import BaseMessages from './BaseMessages.vue';
import BookingSummary from './BookingSummary.vue';
import { handleApiErrors } from '@/utils/apiResponseHandler';
import {
  PhCheck,
  PhCaretLeft,
  PhCaretRight
} from '@phosphor-icons/vue';

export default {
  name: 'MinimalistTemplate',
  components: {
    BaseMessages,
    BookingSummary,
    PhCheck,
    PhCaretLeft,
    PhCaretRight
  },
  props: {
    businessSlug: String,
    business: Object,
    services: Array,
    providers: Array,
    addons: Array,
    isRescheduling: {
      type: Boolean,
      default: false
    },
    existingAppointment: {
      type: Object,
      default: null
    },
    appointmentId: String
  },
  data() {
    return {
      currentStep: 1,
      selectedService: null,
      selectedAddons: [],
      selectedProvider: null,
      selectedDate: null,
      selectedTimeSlot: null,
      additionalInfo: '',
      clientInfo: {
        firstName: '',
        lastName: '',
        email: '',
        phone: ''
      },

      // Calendar data
      currentMonth: new Date().getMonth(),
      currentYear: new Date().getFullYear(),
      dayNames: ['P', 'O', 'T', 'C', 'P', 'S', 'Sv'],

      // Time slots
      availableTimeSlots: [],
      availableDates: {},

      // Loading states
      isLoading: false,
      loadingTimeSlots: false,
      showLoadingSpinner: false,
      loadingTimer: null,

      // Availability data for current month only
      currentMonthAvailability: null,
      loadedMonth: null,
      loadedYear: null,

      // Error handling
      validationError: null,
      apiResponse: null,

      // Step counter - now a data property (default to 3: service + date/time + review)
      displayTotalSteps: 3,
      saveToLocalStorage: false
    }
  },
  computed: {
    totalSteps() {
      return this.isRescheduling ? 4 : 6;
    },
    filteredProviders() {
      if (!this.selectedService) return [];
      return this.providers.filter(provider => {
        return provider.services && provider.services.some(service => service.id === this.selectedService.id);
      });
    },
    filteredAddons() {
      if (!this.selectedService || !this.addons) return [];
      return this.addons.filter(addon => {
        return addon.serviceIds && addon.serviceIds.includes(this.selectedService.id);
      });
    },
    isAddonStepSkipped() {
      return this.filteredAddons.length === 0;
    },
    isProviderStepSkipped() {
      // Provider step is skipped if there's only one provider for the selected service
      return this.selectedService && this.filteredProviders.length === 1;
    },
    displayStep() {
      // Map internal currentStep to user-facing step number
      if (this.isRescheduling) {
        // Rescheduling flow: no service selection step, no addon step
        if (this.isProviderStepSkipped) {
          // Service pre-selected, provider auto-selected
          // Step 2 (date/time) -> display as 1
          // Step 3 (review) -> display as 2
          // Step 4 (confirmation) -> no counter shown
          if (this.currentStep === 2) return 1;
          if (this.currentStep === 3) return 2;
        } else {
          // Service pre-selected, but provider selection needed
          // Step 1 (provider) -> display as 1
          // Step 2 (date/time) -> display as 2
          // Step 3 (review) -> display as 3
          // Step 4 (confirmation) -> no counter shown
          return this.currentStep;
        }
      } else {
        // Normal booking flow
        let stepNumber = 0;

        // Step 1: Service selection always shown
        if (this.currentStep >= 1) stepNumber++;
        if (this.currentStep === 1) return stepNumber;

        // Step 2: Addons (may be skipped)
        if (!this.isAddonStepSkipped && this.currentStep >= 2) stepNumber++;
        if (this.currentStep === 2) return stepNumber;

        // Step 3: Provider selection (may be skipped)
        if (!this.isProviderStepSkipped && this.currentStep >= 3) stepNumber++;
        if (this.currentStep === 3) return stepNumber;

        // Step 4: Date/time
        if (this.currentStep >= 4) stepNumber++;
        if (this.currentStep === 4) return stepNumber;

        // Step 5: Review
        if (this.currentStep >= 5) stepNumber++;
        if (this.currentStep === 5) return stepNumber;

        // Step 6: Confirmation (no counter shown)
        return stepNumber;
      }
      return this.currentStep;
    },
    calendarDays() {
      const days = [];
      const firstDay = new Date(this.currentYear, this.currentMonth, 1);
      const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);
      const firstDayOfWeek = (firstDay.getDay() + 6) % 7;

      for (let i = 0; i < firstDayOfWeek; i++) {
        days.push(null);
      }

      for (let i = 1; i <= lastDay.getDate(); i++) {
        days.push(new Date(this.currentYear, this.currentMonth, i));
      }

      return days;
    },
    currentMonthName() {
      const monthName = new Date(this.currentYear, this.currentMonth, 1).toLocaleString('lv-LV', { month: 'long' });
      return monthName.charAt(0).toUpperCase() + monthName.slice(1);
    },
    cssVariables() {
      const primaryColor = this.business?.colorScheme?.primaryColor || '#590A57';
      const primaryLight = this.business?.colorScheme?.primaryLight || '#F7F3F7';

      return {
        '--main-color': primaryColor,
        '--selected-item-fill-main': primaryLight
      };
    },
    hasSeatData() {
      return this.availableTimeSlots.some(slot => slot.seatNumber != null && slot.takenSeats != null);
    },
    previewMetaItems() {
      const items = [];

      const stripUrl = (url) => url.replace(/^https?:\/\/(www\.)?/, '');

      if (this.business.instagramLink) {
        const showFull = !this.business.facebookLink && !this.business.websiteLink;
        items.push({
          label: showFull ? stripUrl(this.business.instagramLink) : 'instagram',
          href: this.business.instagramLink
        });
      }

      if (this.business.facebookLink) {
        const showFull = !this.business.instagramLink && !this.business.websiteLink;
        items.push({
          label: showFull ? stripUrl(this.business.facebookLink) : 'facebook',
          href: this.business.facebookLink
        });
      }

      if (this.business.websiteLink) {
        const url = this.business.websiteLink;
        const href = url.startsWith('http://') || url.startsWith('https://')
          ? url
          : `https://${url}`;
        const showFull = !this.business.instagramLink && !this.business.facebookLink;
        items.push({
          label: showFull ? stripUrl(href) : 'mājaslapa',
          href: href
        });
      }

      return items;
    }
  },
  watch: {
    currentMonth() {
      this.fetchAvailabilityForCurrentMonth();
    },
    currentYear() {
      this.fetchAvailabilityForCurrentMonth();
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (this.isRescheduling && this.existingAppointment) {
        this.preSelectServiceForReschedule();
        this.preSelectAddonsForReschedule();
        this.autoSelectProviderForReschedule();
      }

      // Initialize displayTotalSteps
      this.calculateTotalSteps();

      // Load saved client info from localStorage
      this.loadClientInfoFromStorage();
    });
  },
  beforeUnmount() {
    // Clear loading timer to prevent memory leak
    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
    }
  },
  methods: {
    calculateTotalSteps() {
      // Total steps excluding confirmation
      // Default assumption: 3 steps (most common case: service + date/time + review)
      // Only increase if there are extra steps (addons or multiple providers)
      if (this.isRescheduling) {
        // Rescheduling: provider selection (or not) + date/time + review = 2 or 3 steps
        this.displayTotalSteps = this.isProviderStepSkipped ? 2 : 3;
      } else if (!this.selectedService) {
        // No service selected yet - default to 3 steps (most users have single provider, no addons)
        this.displayTotalSteps = 3;
      } else {
        // Service is selected - calculate actual steps
        let totalSteps = 3; // service + date/time + review (always present)
        // Add addon step only if there are addons available for the selected service
        if (!this.isAddonStepSkipped) totalSteps++;
        // Add provider step only if there are multiple providers for the selected service
        if (!this.isProviderStepSkipped) totalSteps++;
        this.displayTotalSteps = totalSteps;
      }
    },
    preSelectServiceForReschedule() {
      if (!this.existingAppointment || !this.existingAppointment.service || !this.services) {
        console.warn('Missing data for rescheduling:', {
          existingAppointment: this.existingAppointment,
          services: this.services
        });
        return;
      }

      const serviceId = this.existingAppointment.service.id;
      const matchedService = this.services.find(service => service.id === serviceId);

      if (matchedService) {
        this.selectedService = matchedService;
      } else {
        console.warn('Could not find matching service:', {
          lookingFor: serviceId,
          availableServices: this.services
        });
      }
    },
    autoSelectProviderForReschedule() {
      if (!this.selectedService) {
        return;
      }

      const filteredProviders = this.providers.filter(provider => {
        return provider.services && provider.services.some(service => service.id === this.selectedService.id);
      });

      // If there's only one provider, auto-select them and skip to date/time step
      if (filteredProviders.length === 1) {
        this.selectedProvider = filteredProviders[0];
        this.currentStep = 2; // Jump directly to date/time selection (Step 2 in rescheduling mode)
        this.calculateTotalSteps(); // Recalculate after auto-selecting
        this.fetchAvailabilityForCurrentMonth();
      }
    },
    preSelectAddonsForReschedule() {
      if (!this.existingAppointment?.addons || !this.addons) {
        return;
      }

      // Match existing appointment addons with available addons by ID
      this.selectedAddons = this.existingAppointment.addons
        .map(existingAddon => {
          // Find the full addon object from the addons prop
          const matchedAddon = this.addons.find(addon => addon.id === existingAddon.id);
          // If not found in props, use the addon data from the existing appointment
          return matchedAddon || existingAddon;
        })
        .filter(addon => addon != null);
    },
    handleNextStep() {
      this.validationError = null;

      // Step 1: Service selection
      if (this.currentStep === 1 && !this.isRescheduling) {
        if (!this.selectedService) {
          this.validationError = {
            status: {
              code: 'error',
              messages: ['Lūdzu izvēlieties pakalpojumu']
            }
          };
          return;
        }
      }

      // Step 2: Addons (optional, no validation needed)

      // Step 3: Provider selection (or Step 1 if rescheduling)
      if ((this.currentStep === 3 && !this.isRescheduling) || (this.currentStep === 1 && this.isRescheduling)) {
        if (!this.selectedProvider) {
          this.validationError = {
            status: {
              code: 'error',
              messages: ['Lūdzu izvēlieties speciālistu']
            }
          };
          return;
        }
      }

      // Step 4: Date and time selection (or Step 2 if rescheduling)
      if ((this.currentStep === 4 && !this.isRescheduling) || (this.currentStep === 2 && this.isRescheduling)) {
        if (!this.selectedTimeSlot) {
          this.validationError = {
            status: {
              code: 'error',
              messages: ['Lūdzu izvēlieties datumu un laiku']
            }
          };
          return;
        }
      }

      // If all validations pass, proceed to next step
      this.nextStep();
    },
    nextStep() {
      this.apiResponse = null;
      this.validationError = null;

      if (this.currentStep < this.totalSteps) {
        // After step 1 (service selection), check if we can skip addon and/or provider steps
        if (this.currentStep === 1 && !this.isRescheduling && this.selectedService) {
          const hasAddons = this.filteredAddons.length > 0;
          const filteredProviders = this.providers.filter(provider => {
            return provider.services && provider.services.some(providerService => providerService.id === this.selectedService.id);
          });

          // If no addons and one provider, skip both steps
          if (!hasAddons && filteredProviders.length === 1) {
            if (!this.selectedProvider) {
              this.selectedProvider = filteredProviders[0];
            }
            this.currentStep = 4;
            this.calculateTotalSteps();
            // Reset all availability data to force reload
            this.loadedMonth = null;
            this.loadedYear = null;
            this.currentMonthAvailability = null;
            this.availableDates = {};
            this.availableTimeSlots = [];
            this.selectedDate = null;
            this.selectedTimeSlot = null;
            this.fetchAvailabilityForCurrentMonth();
            this.scrollToTop();
            return;
          }

          // If no addons but multiple providers, skip to provider step
          if (!hasAddons) {
            this.currentStep = 3;
            this.calculateTotalSteps();
            this.scrollToTop();
            return;
          }
        }

        // After step 2 (addons), check if we can skip provider step
        if (this.currentStep === 2 && !this.isRescheduling) {
          const filteredProviders = this.providers.filter(provider => {
            return provider.services && provider.services.some(providerService => providerService.id === this.selectedService.id);
          });

          if (filteredProviders.length === 1) {
            if (!this.selectedProvider) {
              this.selectedProvider = filteredProviders[0];
            }
            this.currentStep = 4;
            this.calculateTotalSteps();
            // Reset all availability data to force reload
            this.loadedMonth = null;
            this.loadedYear = null;
            this.currentMonthAvailability = null;
            this.availableDates = {};
            this.availableTimeSlots = [];
            this.selectedDate = null;
            this.selectedTimeSlot = null;
            this.fetchAvailabilityForCurrentMonth();
            this.scrollToTop();
            return;
          }
        }

        this.currentStep++;
        this.calculateTotalSteps(); // Recalculate on each step forward

        const isDateTimeStep = (this.currentStep === 4 && !this.isRescheduling) ||
          (this.currentStep === 2 && this.isRescheduling);

        if (isDateTimeStep) {
          // Reset all availability data to force reload when entering date/time step
          // This ensures availability is always fresh when user navigates here,
          // even if they went back and selected a different service with the same provider
          this.loadedMonth = null;
          this.loadedYear = null;
          this.currentMonthAvailability = null;
          this.availableDates = {};
          this.availableTimeSlots = [];
          this.selectedDate = null;
          this.selectedTimeSlot = null;
          this.fetchAvailabilityForCurrentMonth();
        }

        this.scrollToTop();
      }
    },
    prevStep() {
      this.apiResponse = null;
      this.validationError = null;

      if (this.currentStep > 1) {
        // Going back from date/time step (4) when provider and/or addon steps were skipped
        if (this.currentStep === 4 && !this.isRescheduling && this.selectedService) {
          const hasAddons = this.filteredAddons.length > 0;
          const filteredProviders = this.providers.filter(provider => {
            return provider.services && provider.services.some(providerService => providerService.id === this.selectedService.id);
          });

          // If both addon and provider steps were skipped, go back to service selection
          if (!hasAddons && filteredProviders.length === 1) {
            this.currentStep = 1;
            this.calculateTotalSteps();
            this.scrollToTop();
            return;
          }

          // If only provider step was skipped, go back to addons
          if (filteredProviders.length === 1) {
            this.currentStep = 2;
            this.calculateTotalSteps();
            this.scrollToTop();
            return;
          }
        }

        // Going back from provider step (3) when addon step was skipped
        if (this.currentStep === 3 && !this.isRescheduling) {
          const hasAddons = this.filteredAddons.length > 0;

          if (!hasAddons) {
            this.currentStep = 1;
            this.calculateTotalSteps();
            this.scrollToTop();
            return;
          }
        }

        // Handle rescheduling back navigation
        if (this.isRescheduling) {
          const filteredProviders = this.providers.filter(provider => {
            return provider.services && provider.services.some(providerService => providerService.id === this.selectedService.id);
          });

          const isComingFromDateTime = this.currentStep === 2;

          if (isComingFromDateTime && filteredProviders.length === 1) {
            return;
          }
        }

        this.currentStep--;
        this.calculateTotalSteps(); // Recalculate on each step backward
        this.scrollToTop();
      }
    },
    selectService(service) {
      this.selectedService = service;
      this.selectedAddons = [];
      this.selectedProvider = null;
      this.currentMonthAvailability = null;
      this.availableDates = {};
      this.loadedMonth = null;
      this.loadedYear = null;

      const filteredProviders = this.providers.filter(provider => {
        return provider.services && provider.services.some(providerService => providerService.id === service.id);
      });

      if (filteredProviders.length === 1) {
        this.selectedProvider = filteredProviders[0];
      }
    },
    toggleAddon(addon) {
      const index = this.selectedAddons.findIndex(a => a.id === addon.id);
      if (index > -1) {
        this.selectedAddons.splice(index, 1);
      } else {
        this.selectedAddons.push(addon);
      }
    },
    selectProvider(provider) {
      this.selectedProvider = provider;
      this.currentMonthAvailability = null;
      this.availableDates = {};
      this.loadedMonth = null;
      this.loadedYear = null;

      // Clear selected date and time slots
      this.selectedDate = null;
      this.selectedTimeSlot = null;
      this.availableTimeSlots = [];

      // If we're on the date/time selection step, fetch new availability immediately
      const isOnDateTimeStep = (this.currentStep === 4 && !this.isRescheduling) ||
        (this.currentStep === 2 && this.isRescheduling);

      if (isOnDateTimeStep) {
        this.fetchAvailabilityForCurrentMonth();
      }
    },
    prevMonth() {
      if (this.currentMonth === 0) {
        this.currentMonth = 11;
        this.currentYear--;
      } else {
        this.currentMonth--;
      }
      this.selectedDate = null;
      this.selectedTimeSlot = null;
    },
    nextMonth() {
      if (this.currentMonth === 11) {
        this.currentMonth = 0;
        this.currentYear++;
      } else {
        this.currentMonth++;
      }
      this.selectedDate = null;
      this.selectedTimeSlot = null;
    },
    async fetchAvailabilityForCurrentMonth() {
      if (!this.selectedService || !this.selectedProvider) {
        return;
      }

      if (this.loadedMonth === this.currentMonth && this.loadedYear === this.currentYear) {
        return;
      }

      this.isLoading = true;
      this.showLoadingSpinner = false;
      this.apiResponse = null;

      this.loadingTimer = setTimeout(() => {
        if (this.isLoading) {
          this.showLoadingSpinner = true;
        }
      }, 300);

      try {
        const monthCode = `${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}`;
        const requestData = {
          serviceId: this.selectedService.id,
          providerId: this.selectedProvider.id,
          businessId: this.business.id,
          monthCode: monthCode,
          addonIds: this.selectedAddons.map(addon => addon.id)
        };

        const response = await this.$http.post(
          `/api/businesses/${this.businessSlug}/availability`,
          requestData
        );

        if (response.data && response.data.code === 'ok') {
          this.currentMonthAvailability = response.data.data;
          this.processAvailabilityData();

          if (this.loadedMonth === null) {
            const allDates = Object.keys(this.currentMonthAvailability || {})
              .filter(dateStr => this.currentMonthAvailability[dateStr]?.length > 0)
              .sort();

            if (allDates.length > 0) {
              const firstAvailableDate = new Date(allDates[0]);
              const firstAvailableMonth = firstAvailableDate.getMonth();
              const firstAvailableYear = firstAvailableDate.getFullYear();

              if (firstAvailableMonth !== this.currentMonth || firstAvailableYear !== this.currentYear) {
                this.currentMonth = firstAvailableMonth;
                this.currentYear = firstAvailableYear;
                this.fetchAvailabilityForCurrentMonth();
                return;
              }
            }
          }

          this.loadedMonth = this.currentMonth;
          this.loadedYear = this.currentYear;

          if (!this.selectedDate) {
            this.autoSelectFirstAvailableDate();
          }
        } else {
          console.error('Error fetching availability:', response.data);
          this.apiResponse = handleApiErrors(
            { response: { data: response.data } },
            'Neizdevās ielādēt pieejamos laikus'
          );
        }
      } catch (error) {
        console.error('Error fetching availability:', error);
        this.apiResponse = handleApiErrors(error, 'Neizdevās ielādēt pieejamos laikus');
      } finally {
        clearTimeout(this.loadingTimer);
        this.isLoading = false;
        this.showLoadingSpinner = false;
      }
    },
    processAvailabilityData() {
      this.availableDates = {};
      if (this.currentMonthAvailability) {
        Object.keys(this.currentMonthAvailability).forEach(dateString => {
          const slots = this.currentMonthAvailability[dateString];
          if (slots && slots.length > 0) {
            this.availableDates[dateString] = true;
          }
        });
      }
    },
    autoSelectFirstAvailableDate() {
      if (this.currentMonthAvailability && Object.keys(this.currentMonthAvailability).length > 0) {
        const dateStrings = Object.keys(this.currentMonthAvailability).sort();
        if (dateStrings.length > 0) {
          const firstAvailableDateString = dateStrings[0];
          const firstAvailableDate = new Date(firstAvailableDateString);

          if (firstAvailableDate.getMonth() === this.currentMonth &&
            firstAvailableDate.getFullYear() === this.currentYear) {
            this.selectDate(firstAvailableDate);
          }
        }
      }
    },
    isDateAvailable(date) {
      if (!date) return false;

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (date < today) return false;

      const dateString = this.formatDateToYYYYMMDD(date);
      return this.availableDates[dateString] === true;
    },
    formatDateToYYYYMMDD(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    async selectDate(date) {
      this.selectedDate = date;
      this.selectedTimeSlot = null;
      this.loadTimeSlots(date);
    },
    async loadTimeSlots(date) {
      if (!date) return;

      this.loadingTimeSlots = true;
      this.availableTimeSlots = [];

      try {
        const dateString = this.formatDateToYYYYMMDD(date);

        if (this.currentMonthAvailability && this.currentMonthAvailability[dateString]) {
          const slots = this.currentMonthAvailability[dateString].map(slot => {
            return {
              id: slot.id,
              startTime: slot.startTime,
              endTime: slot.endTime,
              duration: slot.duration,
              seatNumber: slot.seatNumber,
              takenSeats: slot.takenSeats
            };
          });

          this.availableTimeSlots = slots;
        }
      } catch (error) {
        console.error('Error loading time slots:', error);
        this.apiResponse = handleApiErrors(error, 'Neizdevās ielādēt pieejamos laikus');
      } finally {
        this.loadingTimeSlots = false;
      }
    },
    selectTimeSlot(slot) {
      this.selectedTimeSlot = slot;
    },
    getSeatProgressPercent(slot) {
      if (!slot.seatNumber || slot.seatNumber === 0) return 0;
      return Math.round((slot.takenSeats / slot.seatNumber) * 100);
    },
    isSameDate(date1, date2) {
      if (!date1 || !date2) return false;
      return date1.getDate() === date2.getDate() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getFullYear() === date2.getFullYear();
    },
    formatTime(timeString) {
      if (!timeString) return '';
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    },
    formatPrice(price) {
      return `€${price.toFixed(2)}`;
    },
    formatDuration(minutes) {
      if (!minutes || minutes === 0) {
        return '';
      }
      if (minutes < 60) {
        return `${minutes}min`;
      }
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) {
        return hours === 1 ? '1h' : `${hours}h`;
      }
      const hourLabel = hours === 1 ? '1h' : `${hours}h`;
      return `${hourLabel} ${remainingMinutes}min`;
    },
    async confirmBooking() {
      this.apiResponse = null;

      const validationErrors = [];

      if (!this.selectedService) {
        validationErrors.push('Lūdzu izvēlieties pakalpojumu');
      }

      if (!this.selectedProvider) {
        validationErrors.push('Lūdzu izvēlieties speciālistu');
      }

      if (!this.selectedDate) {
        validationErrors.push('Lūdzu izvēlieties datumu');
      }

      if (!this.selectedTimeSlot) {
        validationErrors.push('Lūdzu izvēlieties laiku');
      }

      if (!this.clientInfo.firstName || !this.clientInfo.firstName.trim()) {
        validationErrors.push('Lūdzu ievadiet vārdu');
      }

      if (!this.clientInfo.lastName || !this.clientInfo.lastName.trim()) {
        validationErrors.push('Lūdzu ievadiet uzvārdu');
      }

      if (!this.clientInfo.email || !this.clientInfo.email.trim()) {
        validationErrors.push('Lūdzu ievadiet e-pasta adresi');
      }

      if (!this.clientInfo.phone || !this.clientInfo.phone.trim()) {
        validationErrors.push('Lūdzu ievadiet telefona numuru');
      }

      if (validationErrors.length > 0) {
        this.apiResponse = {
          status: {
            code: 'error',
            messages: validationErrors
          }
        };
        return;
      }

      const bookingData = {
        service: this.selectedService,
        selectedAddons: this.selectedAddons,
        provider: this.selectedProvider,
        date: this.selectedDate,
        timeSlot: this.selectedTimeSlot,
        additionalInfo: this.additionalInfo,
        clientInfo: this.clientInfo
      };

      try {
        const response = await this.$http.post(
          `/api/businesses/${this.businessSlug}/book/`,
          bookingData
        );

        if (response?.data?.code === 'ok') {
          this.saveClientInfoToStorage();
          this.nextStep();
        }
      } catch (error) {
        console.error('Error creating booking:', error);
        this.apiResponse = handleApiErrors(error, 'Neizdevās izveidot pierakstu');
      }
    },
    async confirmReschedule() {
      this.apiResponse = null;

      const validationErrors = [];

      if (!this.selectedProvider) {
        validationErrors.push('Lūdzu izvēlieties speciālistu');
      }

      if (!this.selectedDate) {
        validationErrors.push('Lūdzu izvēlieties datumu');
      }

      if (!this.selectedTimeSlot) {
        validationErrors.push('Lūdzu izvēlieties laiku');
      }

      if (validationErrors.length > 0) {
        this.apiResponse = {
          status: {
            code: 'error',
            messages: validationErrors
          }
        };
        return;
      }

      const rescheduleData = {
        appointmentId: this.appointmentId,
        date: this.selectedDate,
        timeSlot: this.selectedTimeSlot,
        provider: this.selectedProvider,
        additionalInfo: this.additionalInfo,
        token: this.$route.query.token
      };

      try {
        const response = await this.$http.post(
          `/api/businesses/${this.businessSlug}/reschedule/`,
          rescheduleData
        );

        if (response?.data?.code === 'ok') {
          this.nextStep();
        } else {
          this.apiResponse = handleApiErrors(
            { response: { data: response.data } },
            'Neizdevās pārplānot pierakstu'
          );
        }
      } catch (error) {
        console.error('Error rescheduling appointment:', error);
        this.apiResponse = handleApiErrors(error, 'Neizdevās pārplānot pierakstu');
      }
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    },
    loadClientInfoFromStorage() {
      try {
        const savedClientInfo = localStorage.getItem('appointum_client_info');
        if (savedClientInfo) {
          const parsed = JSON.parse(savedClientInfo);
          this.clientInfo.firstName = parsed.firstName || '';
          this.clientInfo.lastName = parsed.lastName || '';
          this.clientInfo.email = parsed.email || '';
          this.clientInfo.phone = parsed.phone || '';
          this.saveToLocalStorage = true;
        }
      } catch (error) {
        console.error('Error loading client info from localStorage:', error);
      }
    },
    saveClientInfoToStorage() {
      try {
        if (this.saveToLocalStorage) {
          const clientInfoToSave = {
            firstName: this.clientInfo.firstName,
            lastName: this.clientInfo.lastName,
            email: this.clientInfo.email,
            phone: this.clientInfo.phone
          };
          localStorage.setItem('appointum_client_info', JSON.stringify(clientInfoToSave));
        } else {
          localStorage.removeItem('appointum_client_info');
        }
      } catch (error) {
        console.error('Error saving client info to localStorage:', error);
      }
    }
  },
  emits: ['book-appointment', 'reschedule-appointment']
}
</script>

<style scoped>
.minimalist-booking {
  max-width: 800px;
  margin: 48px auto 0 auto;
  padding: 0 20px 20px 20px;
}

.booking-header {
  text-align: center;
}

.booking-header h1 {
  font-weight: 400;
  font-size: 48px;
  margin-top: 0;
  margin-bottom: 0;
}

.current-appointment-card {
  background-color: var(--selected-item-fill-main);
  border-radius: 24px;
  border: none;
  padding: 30px;
  text-align: left;
  margin-top: 20px;
}

/* Step container */
.step-container {
  background-color: var(--background-table);
  border-radius: 20px;
  padding: 32px;
  margin-bottom: 20px;
  margin-top: 40px;
}

.business-avatar {
  margin-bottom: 10px;
}

.business-description {
  padding: 0 50px;
  font-size: 18px;
}

.confirmation-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.confirmation-header h2 {
  margin: 0;
  font-weight: 400;
  font-size: 32px;
}

.confirmation-subtitle {
  font-size: 16px;
  font-weight: 300;
}

.addon-subtitle {
  font-size: 16px;
  font-weight: 300;  
  margin-top: 10px;
  margin-bottom: 38px;
}

.checkmark-circle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background-color: #036D19;
  border-radius: 50%;
  color: white;
  flex-shrink: 0;
}

.checkmark-circle svg {
  width: 14px;
  height: 14px;
}

.step-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 48px;
}

.step-header-w-subtitle {
  margin-bottom: 0;
}

.step-header h2 {
  margin: 0;
  font-weight: 400;
  font-size: 32px;
}

.step-counter {
  font-size: 16px;
  color: var(--main-color);
  font-weight: 400;
}

.service-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 20px;
}

.service-card {
  border: 1px solid var(--border-color);
  border-radius: 24px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
}

.service-card.selected {
  border-color: var(--main-color);
  background-color: var(--selected-item-fill-main);
}

.service-info {
  flex: 1;
}

.service-info h3 {
  margin-top: 0;
  margin-bottom: 5px;
  font-weight: 400;
}

/* Service header with name and price on same row */
.service-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 5px;
  gap: 20px;
}

.service-header h3 {
  margin: 0;
  font-weight: 400;
}

.service-price {
  color: var(--text-color-secondary);
  font-size: 16px;
  font-weight: 400;
}

.service-info p {
  margin-bottom: 10px;
  color: var(--text-color-secondary);
  font-size: 14px;
}

.service-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--text-color-secondary);
  font-size: 14px;
}

.service-meta .duration {
  display: inline-block;
}

.service-meta .mobile-price {
  display: none;
}

/* Provider cards */
.provider-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.provider-card {
  border: 1px solid var(--border-color);
  border-radius: 24px;
  padding: 24px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.provider-card.selected {
  border-color: var(--main-color);
  background-color: var(--selected-item-fill-main);
}

.provider-info {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.provider-info h3 {
  margin: 0 0 5px 0;
  font-weight: 400;
}

.provider-info p {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 14px;
}

/* Calendar and Date Picker */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px;
}

.loading-spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid var(--main-color);
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

.loading-spinner.small {
  width: 20px;
  height: 20px;
  border-width: 3px;
  margin-bottom: 10px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.social-media-link {
  color: var(--main-color);
}

.no-slots-message {
  padding: 15px;
  color: #e74c3c;
  text-align: center;
  background-color: #fef0f0;
  border-radius: 4px;
}

.placeholder-message {
  padding: 15px;
  color: var(--text-color-secondary);
  text-align: center;
}

.date-picker {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.calendar {
  flex: 1;
  max-width: 400px;
  flex-shrink: 0;
}

.month-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.month-navigation h3 {
  font-weight: 400;
  margin: 0;
  font-size: 20px;
}

.month-navigation-buttons {
  display: flex;
  gap: 10px;
}

.month-navigation button {
  background: none;
  border: 1px solid var(--selected-item-background);
  border-radius: 4px;
  cursor: pointer;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 5px;
  justify-items: center;
}

.day-header {
  text-align: center;
  font-weight: 500;
  font-size: 14px;
  color: var(--text-color-secondary);
  padding: 5px 0;
}

.calendar-day {
  aspect-ratio: 1;
  width: 100%;
  min-width: 32px;
  max-width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  justify-self: center;
}

.calendar-day.empty {
  cursor: default;
}

.calendar-day.selected {
  background-color: var(--main-color);
  color: white;
}

.calendar-day.unavailable {
  color: #ccc;
  cursor: not-allowed;
}

/* Time slots */
.time-slots {
  flex: 1;
  min-width: 0;
}

.time-slots h3 {
  margin-top: 0;
  margin-bottom: 24px;
  font-weight: 400;
  font-size: 20px;
}

.time-slot-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 10px;
}

.time-slot {
  padding: 8px;
  border-radius: 50px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: var(--background-color);
}

.time-slot.selected {
  background-color: var(--main-color);
  color: white;
}

/* Time slots with seat information */
.time-slot-grid.has-seats {
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
}

.time-slot.with-seats {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
}

.time-slot-time {
  white-space: nowrap;
}

.seat-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.seat-progress-container {
  width: 40px;
  height: 6px;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
  overflow: hidden;
}

.time-slot.selected .seat-progress-container {
  background-color: rgba(255, 255, 255, 0.3);
}

.seat-progress-bar {
  height: 100%;
  background-color: var(--main-color);
  border-radius: 3px;
  transition: width 0.2s ease;
}

.time-slot.selected .seat-progress-bar {
  background-color: white;
}

.seat-text {
  font-size: 12px;
  white-space: nowrap;
  color: var(--text-color-secondary);
  width: 110px;
}

.time-slot.selected .seat-text {
  color: white;
}

/* Booking summary */
.booking-summary-margins {
  margin-bottom: 30px;
}

.summary-item {
  display: flex;
  margin-bottom: 10px;
  font-size: 15px;
}

/* Client info form */
.client-info h3 {
  margin-top: 0;
  margin-bottom: 15px;
  font-weight: 400;
}

.form-row {
  display: flex;
  gap: 20px;
}

.form-group {
  flex: 1;
}

/* Additional info */
.additional-info {
  margin-bottom: 30px;
}

.additional-info h3 {
  margin-top: 0;
  margin-bottom: 10px;
  font-weight: 400;
}

/* Navigation buttons */
.step-navigation {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 48px;
}

.step-navigation:has(.back-button) {
  justify-content: space-between;
}

/* Confirmation */
.confirmation h2 {
  margin-bottom: 10px;
}

.confirmation-subtitle {
  margin-top: -5px;
}

.confirmation-details {
  margin: 30px 0 0 0;
  text-align: left;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 0;
  cursor: pointer;
}

.checkbox-text {
  font-size: 16px;
  line-height: 1.4;
}

.business-meta-line {
  margin-top: 6px;
  margin-bottom: 24px;
  font-size: 16px;
}

.meta-divider {
  color: var(--text-color-secondary);
  user-select: none;
  margin: 0 3px;
  font-size: 14px;
  font-weight: 400;
}

.business-avatar img {
  width: 140px;
  height: 140px;
  border-radius: 50%;
  object-fit: cover;
}

.no-services {
  color: var(--text-color-secondary);
  margin-bottom: 40px;
  justify-content: center;
  text-align: center;
  font-style: italic;
}

.compact-booking-header,
.compact-step-container {
  margin-top: 30px;
}

/* Tablets and small desktops (768px and below) */
@media (max-width: 768px) {
  .minimalist-booking {
    padding: 1rem;
    margin: 20px auto 0 auto;
  }

  .step-container {
    padding: 2rem;
  }

  .service-header {
    margin-bottom: 8px;
  }

  .service-price {
    display: none;
  }

  .service-meta .mobile-price {
    display: inline-block;
    color: var(--text-color-secondary);
    font-size: 14px;
    font-weight: 500;
  }

  .form-row {
    flex-direction: column;
    gap: 0 !important;
  }

  .service-list,
  .provider-list {
    grid-template-columns: 1fr;
  }
}

/* Time slots: Switch to 3 columns on screens smaller than 640px */
@media (max-width: 640px) {
  .time-slot-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .time-slot-grid.has-seats {
    grid-template-columns: 1fr;
  }
}

/* Stack calendar and time slots vertically at 540px */
@media (max-width: 540px) {
  .date-picker {
    flex-direction: column;
  }

  .calendar {
    max-width: 100%;
  }
}

/* Small mobile phones (480px and below) */
@media (max-width: 480px) {
  .booking-header h1 {
    margin-top: 0;
    margin-bottom: 0;
    font-size: 32px;
  }

  .compact-booking-header {
    margin: 10px auto 0 auto;
  }

  .step-container {
    padding: 1.5rem 1rem;
    margin-top: 32px;
  }

  .compact-step-container {
    margin-top: 16px;
  }

  .business-description {
    padding: 0;
  }

  .current-appointment-card {
    padding: 1.5rem 1rem;
  }

  .business-meta-line {
    margin-top: 2px;
    margin-bottom: 20px;
  }

  .step-header h2 {
    width: 100%;
    margin-bottom: 20px;
    font-size: 22px;
  }

  .confirmation-header h2 {
    margin: 0;
    font-weight: 400;
    font-size: 22px;
  }

  .step-counter {
    display: none;
  }

  .calendar-day {
    min-width: 28px;
    max-width: 36px;
    font-size: 14px;
  }

  .calendar-grid {
    gap: 3px;
  }

  .time-slot {
    font-size: 14px;
  }

  .step-navigation {
    gap: 10px;
  }

  .step-navigation:not(:has(.back-button)) button {
    width: 100%;
  }

  .step-navigation:has(.back-button) button {
    flex: 1;
  }

  .current-appointment-card .summary-item.stacked {
    flex-direction: column;
    margin-bottom: 15px;
  }

  .current-appointment-card .summary-item.stacked .summary-label {
    width: auto;
    margin-bottom: 5px;
  }

  .confirmation-details {
    margin: 0;
  }

  .service-header {
    margin-bottom: 5px;
  }

  .service-info p {
    margin-top: 0;
    line-height: 16px;
  }

  .service-card {
    border-radius: 16px;
    padding: 12px 16px 16px 16px;
  }

  .provider-list,
  .service-list {
    gap: 10px;
  }

  .step-header {
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    margin-bottom: 5px;
  }

  .step-header-w-subtitle,
  .step-header-w-subtitle h2 {
    margin-bottom: 0;
  }

  .addon-subtitle {
    text-align: center;
    margin-bottom: 20px;
  }

  .provider-card {
    border-radius: 16px;
    padding: 16px;
  }

  .time-slots h3,
  .month-navigation h3 {
    font-size: 18px;
  }

  .business-meta-line {
    font-size: 14px;
  }

  .business-description {
    font-size: 16px;
  }

  .business-avatar img {
    width: 120px;
    height: 120px;
  }
}

/* Very small screens (320px and below) */
@media (max-width: 320px) {
  .calendar-day {
    min-width: 24px;
    max-width: 32px;
    font-size: 11px;
  }

  .calendar-grid {
    gap: 2px;
  }
}

@media (hover: hover) and (pointer: fine) {

  .calendar-day.available:hover,
  .time-slot:hover {
    background-color: var(--selected-item-fill-main);
  }

  .calendar-day.selected:hover,
  .time-slot.selected:hover {
    background-color: var(--main-color);
    color: white;
  }

  .service-card:hover,
  .provider-card:hover {
    border-color: var(--main-color);
  }
}
</style>